/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import template from './political-entity-edit-page.component.html';

import {APP_SHARED} from '../common/app-shared';
import {CountrySelectComponent} from '../common/directives/country-select/country-select';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {PoliticalEntityApiService, PoliticalEntityModel} from './political-entity-api.service';

const POLITICAL_ENTITY_QUERY_PAGE_SIZE = 2000;

function isPresent(value: any): boolean {
  return value !== null && value !== undefined;
}

function unwrapDomainRef(value: any): any {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id')) {
    return value.id;
  }
  return value;
}

function stashSponsorParentForCrumbs(m: any): void {
  if (m.type !== 'SPONSOR') {
    delete m._politicalEntityCrumbParent;
    return;
  }
  const parentRaw = isPresent(m.parent) ? m.parent : m.parentId;
  if (parentRaw && typeof parentRaw === 'object' && parentRaw.id) {
    m._politicalEntityCrumbParent = {id: parentRaw.id, name: parentRaw.name};
  } else if (isPresent(parentRaw) && parentRaw !== '') {
    m._politicalEntityCrumbParent = {id: unwrapDomainRef(parentRaw), name: ''};
  } else {
    delete m._politicalEntityCrumbParent;
  }
}

function normaliseEntityRefs(m: any): void {
  stashSponsorParentForCrumbs(m);
  m.ownerTeam = unwrapDomainRef(isPresent(m.ownerTeam) ? m.ownerTeam : m.ownerTeamId);
  m.parent = unwrapDomainRef(isPresent(m.parent) ? m.parent : m.parentId);
  delete m.ownerTeamId;
  delete m.parentId;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, ButtonsModule, CountrySelectComponent, ...FORM_CONTROLS],
  selector: 'adn-political-entity-edit-page',
  template
})
export class PoliticalEntityEditPageComponent extends SignalAdminResourceEditPageBase<PoliticalEntityModel> {
  private readonly politicalEntityApi = inject(PoliticalEntityApiService);

  readonly entityTypes = ['CONTROLLER', 'SPONSOR', 'PAYER'];
  readonly legalEntityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];
  readonly visibilityTypes = ['STANDARD', 'TEST', 'PRIVATE', 'PRIVATE_ADN'];

  showVisibilityType = false;
  private readonly availableTeamsState = signal<any[]>([]);
  private readonly availableParentEntitiesState = signal<any[]>([]);
  get availableTeams(): any[] { return this.availableTeamsState(); }
  set availableTeams(value: any[]) { this.availableTeamsState.set(value); }
  get availableParentEntities(): any[] { return this.availableParentEntitiesState(); }
  set availableParentEntities(value: any[]) { this.availableParentEntitiesState.set(value); }

  constructor(
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService
  ) {
    super(inject(PoliticalEntityApiService), 'politicalEntity');
    this.showVisibilityType = this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');
  }

  protected override initialiseModel(model: PoliticalEntityModel): PoliticalEntityModel {
    const initialised = super.initialiseModel(model);
    initialised.type = initialised.type || 'CONTROLLER';
    initialised.visibilityType = initialised.visibilityType || 'STANDARD';
    initialised.address = initialised.address || {};
    normaliseEntityRefs(initialised);
    this.loadTeams();
    this.loadParentEntities(initialised.id);
    return initialised;
  }

  protected override afterSave(model: PoliticalEntityModel): void {
    normaliseEntityRefs(model);
    this.loadTeams();
    this.loadParentEntities(model.id);
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  trackByValue(_: number, value: string): string {
    return value;
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  private loadTeams(): void {
    this.teamApi.listOptions({minimal: true, pageSize: 2000}).subscribe((page) => {
      this.availableTeams = (page.results || []).filter((team: any) => {
        return team.type === 'CONTROLLER' || team.type === 'SPONSOR';
      });
    }, () => {
      this.availableTeams = [];
    });
  }

  private loadParentEntities(currentEntityId: string): void {
    this.politicalEntityApi.listAll(POLITICAL_ENTITY_QUERY_PAGE_SIZE).subscribe({
      next: (page) => {
        this.availableParentEntities = (page.results || []).filter((entity: any) => {
          return entity.type === 'CONTROLLER' && entity.id !== currentEntityId;
        });
      },
      error: () => {
        this.availableParentEntities = [];
      }
    });
  }
}
